import { get, post } from "@/lib/api";
import type {
  CompanyInfo,
  StatusResponse,
  EventItem,
  AddEventRequest,
  AddEventResponse,
  ConvertEventToPickRequest,
  ConvertEventToIreRequest,
  DeleteEventRequest,
  CompleteEventRequest,
  UpdateEventRequest,
  AccessPointItem,
  SaveAccessPointRequest,
  SaveAccessPointResponse,
  AssignDriverRequest,
  UnassignDriverRequest,
  CompanyVehicleOption,
  SetTaskVehicleRequest,
  SendDriverMessageRequest,
  DriverLocationItem,
  NotificationUpdateResponse,
  LocationRequest,
  DriverItem,
  CastItem,
  ActionLogItem,
  DriverTrackPoint,
  StaffCandidateItem,
} from "@/types/dispatch_sys_api_def";

// ■■■■■■■ API通信層 ■■■■■■■

// GET /api/dispatch-sys/company-info - 所属企業基礎情報取得
export const getCompanyInfo = async (): Promise<CompanyInfo> => {
  return await get<CompanyInfo>("/api/dispatch-sys/company-info");
};

// GET /api/dispatch-sys/drivers - ドライバー一覧取得
export const getDrivers = async (date?: string, area?: string): Promise<DriverItem[]> => {
  const qs = [date ? `date=${date}` : "", area ? `area=${area}` : ""].filter(Boolean).join("&");
  return await get<DriverItem[]>(`/api/dispatch-sys/drivers${qs ? `?${qs}` : ""}`);
};

// GET /api/dispatch-sys/areas - 配車ボードで切替可能なエリア一覧(#62)
export type DispatchAreaOption = { code: string; label: string };
// current = 前回このボードで選んだエリア。リロード/再入場時の初期選択復元用(ISSUE#186)。空=既定にフォールバック
export const getDispatchAreas = async (): Promise<{ areas: DispatchAreaOption[]; unrestricted: boolean; current: string }> => {
  return await get<{ areas: DispatchAreaOption[]; unrestricted: boolean; current: string }>(`/api/dispatch-sys/areas`);
};

// POST /api/dispatch-sys/areas/current - 選んだ表示エリアを保存(リロード後も保持・ISSUE#186)
export const saveCurrentDispatchArea = async (area: string): Promise<StatusResponse> => {
  return await post<StatusResponse>(`/api/dispatch-sys/areas/current`, { area });
};

// GET /api/dispatch-sys/staff-candidates - ドライバー一覧に追加できる内勤(スタッフ)候補一覧（その日に配車可能なスタッフのみ）
export const getStaffCandidates = async (date?: string, area?: string): Promise<StaffCandidateItem[]> => {
  const qs = [date ? `date=${date}` : "", area ? `area=${area}` : ""].filter(Boolean).join("&");
  return await get<StaffCandidateItem[]>(`/api/dispatch-sys/staff-candidates${qs ? `?${qs}` : ""}`);
};

// GET /api/dispatch-sys/casts - 当日出勤キャスト一覧取得
export const getCasts = async (date?: string): Promise<CastItem[]> => {
  const params = date ? `?date=${date}` : "";
  return await get<CastItem[]>(`/api/dispatch-sys/casts${params}`);
};

// GET /api/dispatch-sys/events - イベント一覧取得
export const getEvents = async (date?: string, area?: string): Promise<EventItem[]> => {
  const qs = [date ? `date=${date}` : "", area ? `area=${area}` : ""].filter(Boolean).join("&");
  return await get<EventItem[]>(`/api/dispatch-sys/events${qs ? `?${qs}` : ""}`);
};

// GET /api/dispatch-sys/event/add - 新規イベント追加
export const addEvent = async (params: AddEventRequest): Promise<AddEventResponse> => {
  return await post<AddEventResponse>("/api/dispatch-sys/event/add", params);
};

// POST /api/dispatch-sys/event/convert-to-pick - 入れイベントをピックに変換（インコール発動／インコール忘れ対応）
export const convertEventToPick = async (params: ConvertEventToPickRequest): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/dispatch-sys/event/convert-to-pick", params);
};

// POST /api/dispatch-sys/event/convert-to-ire - ピックイベントを入れに戻す（インコール取消）
export const convertEventToIre = async (params: ConvertEventToIreRequest): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/dispatch-sys/event/convert-to-ire", params);
}

// POST /api/dispatch-sys/event/delete - 手動イベントを論理削除（手動カードのみ）
export const deleteEvent = async (params: DeleteEventRequest): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/dispatch-sys/event/delete", params);
};

// POST /api/dispatch-sys/event/complete - 手動イベントを完了（薄表示で残す）
export const completeEvent = async (params: CompleteEventRequest): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/dispatch-sys/event/complete", params);
};

// POST /api/dispatch-sys/event/update - 未割当の手動イベントのキャスト・時間を編集（#114）
export const updateEvent = async (params: UpdateEventRequest): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/dispatch-sys/event/update", params);
};

// GET /api/dispatch-sys/access/list - 経由地点一覧取得
export const getAccessList = async (): Promise<AccessPointItem[]> => {
  return await get<AccessPointItem[]>("/api/dispatch-sys/access/list");
};

// POST /api/dispatch-sys/access/save - 経由地点を保存
export const saveAccessPoint = async (params: SaveAccessPointRequest): Promise<SaveAccessPointResponse> => {
  return await post<SaveAccessPointResponse>("/api/dispatch-sys/access/save", params);
};

// POST /api/dispatch-sys/assign - イベントをドライバーに割り振り
export const assignDriver = async (params: AssignDriverRequest): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/dispatch-sys/assign", params);
};

// POST /api/dispatch-sys/unassign - 担当ドライバーを解除して未割当に戻す
export const unassignDriver = async (params: UnassignDriverRequest): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/dispatch-sys/unassign", params);
};

// GET /api/dispatch-sys/company-vehicles - 社用車選択欄の候補一覧(#105)
export const getCompanyVehicles = async (area?: string): Promise<CompanyVehicleOption[]> => {
  const qs = area ? `?area=${area}` : "";
  return await get<CompanyVehicleOption[]>(`/api/dispatch-sys/company-vehicles${qs}`);
};

// POST /api/dispatch-sys/task-vehicle - 送迎タスクへ社用車を割り当てる/外す(#105)
export const setTaskVehicle = async (params: SetTaskVehicleRequest): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/dispatch-sys/task-vehicle", params);
};

// POST /api/dispatch-sys/driver-message - 配車係からドライバーへメッセージ送信
export const sendDriverMessage = async (params: SendDriverMessageRequest): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/dispatch-sys/driver-message", params);
};

// GET /api/dispatch-sys/locations - ドライバーの現在位置情報取得
export const getLocations = async (area?: string): Promise<DriverLocationItem[]> => {
  const areaParam = area ? `?area=${area}` : "";
  return await get<DriverLocationItem[]>(`/api/dispatch-sys/locations${areaParam}`);
};

// GET /api/dispatch-sys/action-logs - 行動ログ一覧取得
export const getActionLogs = async (date: string, type?: string): Promise<ActionLogItem[]> => {
  const params = new URLSearchParams({ date });
  if (type && type !== "ALL") {
    params.append("type", type);
  }
  return await get<ActionLogItem[]>(`/api/dispatch-sys/action-logs?${params.toString()}`);
};

// GET /api/dispatch-sys/driver-track - ドライバー走行ログ取得
export const getDriverTrack = async (driverId: string, date: string): Promise<DriverTrackPoint[]> => {
  const params = new URLSearchParams({ driverId, date });
  return await get<DriverTrackPoint[]>(`/api/dispatch-sys/driver-track?${params.toString()}`);
};

// ■■■■■■■ Notification API通信層 ■■■■■■■

// GET /api/notification/update - 対象ユーザに新規通知があるかを確認する(ロングポーリング形式)
export const getNotificationUpdate = async (): Promise<NotificationUpdateResponse> => {
  return await get<NotificationUpdateResponse>("/api/notification/update");
};

// POST /api/notification/location - ユーザの現在位置をサーバに通知する
export const postLocation = async (params: LocationRequest): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/notification/location", params);
};
